import React from 'react'
import { Column, Row } from 'react-table'
import Table from '../table'
import { JobTitle } from '../../api/job-titles'
import { InterimAgency } from '../../api/organizations'
import { Button, Icon } from 'react-bulma-components'
import { EditIcon } from '../icons'

interface LaborCostsCoefficientsTableProps {
  jobTitles: JobTitle[]
  interimAgency: InterimAgency
  onEdit?: (
    jobTitleId: string,
    agencyId: string,
    laborCostCoefficient: JobTitle['interimAgencySettings'][0],
  ) => void
}

const LaborCostsCoefficientsTable: React.FC<LaborCostsCoefficientsTableProps> = ({
  jobTitles,
  interimAgency,
  onEdit,
}) => {
  const tableColumns = useTableColumns(interimAgency, onEdit)

  const tableData = jobTitles.map(jobTitle => {
    const coefficient = jobTitle.interimAgencySettings.find(
      c => c.interimAgency === interimAgency._id,
    )

    return {
      jobTitle,
      _id: jobTitle._id,
      managementValue: coefficient?.coefficients.managementValue ?? '-',
      delegationValue: coefficient?.coefficients.delegationValue ?? '-',
      hourlyRateSettings: coefficient?.hourlyRateSettings,
    }
  })

  return <Table columns={tableColumns} data={tableData} noDataMessage="Aucun métier à afficher" />
}

const useTableColumns = (
  interimAgency: InterimAgency,
  onEdit?: LaborCostsCoefficientsTableProps['onEdit'],
): Column[] => {
  return React.useMemo(
    () =>
      [
        {
          Header: `Métier / ${interimAgency.name}`,
          accessor: 'jobTitle',
          Cell: ({ value: jobTitle }: { value: JobTitle }) => jobTitle.name,
        },
        {
          Header: 'Délégation',
          accessor: 'delegationValue',
          className: 'has-text-centered',
        },
        {
          Header: 'Gestion',
          accessor: 'managementValue',
          className: 'has-text-centered',
        },
        {
          Header: 'Cout horaire',
          accessor: 'hourlyRateSettings',
          className: 'has-text-centered',
          Cell: ({
            value: hourlyRateSettings,
          }: {
            value: JobTitle['interimAgencySettings'][0]['hourlyRateSettings']
          }) => {
            if (!hourlyRateSettings) return 'N/A'
            if (hourlyRateSettings.useSmic) {
              return 'SMIC'
            }
            return `${hourlyRateSettings.min} € - ${hourlyRateSettings.max} €`
          },
        },
        onEdit && {
          Header: '',
          id: 'actions',
          accessor: 'jobTitle',
          Cell: ({ value: jobTitle, row }: { value: JobTitle; row: Row<JobTitle> }) => (
            <Button
              onClick={() => onEdit?.(jobTitle._id, interimAgency._id, row.original as any)}
              color="primary"
              outlined
              size="small"
              data-test={`edit-job-title-${jobTitle._id}`}
            >
              <Icon>
                <EditIcon className="h-5 w-5" />
              </Icon>
            </Button>
          ),
          className: 'has-text-right',
        },
      ].filter(Boolean) as Column[],
    [interimAgency, onEdit],
  )
}

export default LaborCostsCoefficientsTable
